import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { animateDetailsToggle, cssInterpolatesHeight, motionAllowed } from '@theme/r-motion';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#openFromHash();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    window.addEventListener('hashchange', this.#openFromHash, { signal });
  }

  /* ↗ .cursor/references/code-notes/accordion-custom.js.md#§5 */
  #openFromHash = () => {
    const id = window.location.hash.slice(1);
    if (!id) return;

    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(id));
    } catch {
      return;
    }
    if (!target || !this.contains(target)) return;

    if (!this.details.open) {
      this.details.classList.add('accordion--no-animation');
      this.details.open = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.details.classList.remove('accordion--no-animation');
        });
      });
    }
    requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
  };

  /* ↗ .cursor/references/code-notes/accordion-custom.js.md#§2 */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // ↗ .cursor/references/code-notes/accordion-custom.js.md#§1
    if (cssInterpolatesHeight || !motionAllowed()) return;
    if (!this.details.querySelector(':scope > .details-content')) return;

    event.preventDefault();
    animateDetailsToggle(this.details);
  };

  /* ↗ .cursor/references/code-notes/accordion-custom.js.md#§3 */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /* ↗ .cursor/references/code-notes/accordion-custom.js.md#§4 */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    const open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    if (open === this.details.open) return;

    // This toggle is programmatic (initial state or a breakpoint change), not a user
    // interaction — suppress the open/close transition for it
    this.details.classList.add('accordion--no-animation');
    this.details.open = open;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.details.classList.remove('accordion--no-animation');
      });
    });
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
